// EstadoResultadosXL - Sprint 44 Fase 3 Vista 8.
// Reporte estado de resultados por rango de fechas. Solo lectura.
import { useCallback, useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import * as svc from '../services/contabilidadService';
import XLInput from '../../../components/xl/XLInput';
import XLSkeleton from '../../../components/xl/XLSkeleton';
import { fmt } from '../utils';

const CSS_ID = 'xl-rep-er-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-tools { display: flex; align-items: end; gap: 10px; flex-wrap: wrap }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
}
.xl-btn:hover { background: var(--xl-color-primary-hover) }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-banner-err {
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
  padding: 10px 14px; margin-bottom: 16px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}

.xl-er-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 18px;
  margin-bottom: 16px;
}
.xl-er-card__hd {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 10px; margin-bottom: 10px;
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-er-card__title {
  font-size: 12px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
}
.xl-er-card__title--ingreso { color: var(--xl-color-success) }
.xl-er-card__title--gasto   { color: var(--xl-color-danger) }
.xl-er-card__total { font-family: var(--xl-font-mono); font-weight: 600; font-size: 14px }
.xl-er-card__total--ingreso { color: var(--xl-color-success) }
.xl-er-card__total--gasto   { color: var(--xl-color-danger) }
.xl-er-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 0; font-size: 13px;
}
.xl-er-code { font-family: var(--xl-font-mono); font-size: 11px; color: var(--xl-color-text-muted); margin-right: 8px }
.xl-er-empty { font-size: 12px; color: var(--xl-color-text-muted) }

.xl-utilidad {
  border-radius: var(--xl-radius-lg); border-width: 2px; border-style: solid;
  padding: 18px; text-align: center;
}
.xl-utilidad--pos {
  background: rgba(5,150,105,0.08); border-color: rgba(5,150,105,0.42);
}
.xl-utilidad--neg {
  background: rgba(220,38,38,0.08); border-color: rgba(220,38,38,0.42);
}
[data-xl-theme="dark"] .xl-utilidad--pos { background: rgba(16,185,129,0.14); border-color: rgba(16,185,129,0.45) }
[data-xl-theme="dark"] .xl-utilidad--neg { background: rgba(248,113,113,0.14); border-color: rgba(248,113,113,0.45) }
.xl-utilidad__lbl {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--xl-color-text-muted); margin-bottom: 6px;
}
.xl-utilidad__val { font-family: var(--xl-font-mono); font-size: 28px; font-weight: 700 }
.xl-utilidad__val--pos { color: var(--xl-color-success) }
.xl-utilidad__val--neg { color: var(--xl-color-danger) }
.xl-loading { display: flex; flex-direction: column; gap: 10px }
`;

export default function EstadoResultadosXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const firstDay = today.slice(0, 7) + '-01';
  const [desde, setDesde] = useState(firstDay);
  const [hasta, setHasta] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await svc.estadoResultados(desde, hasta)); }
    catch (e) { setErr((e && e.message) || 'Error'); setData(null); }
    finally { setLoading(false); }
  }, [desde, hasta]);

  useEffect(() => { load(); }, [load]);

  const utilidadPos = data && Number(data.utilidad) >= 0;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Estado de resultados</h2>
          <p className="xl-page__sub">Ingresos \u2212 gastos en un periodo.</p>
        </div>
        <div className="xl-tools">
          <XLInput label="Desde" type="date" value={desde}
            onChange={(e) => setDesde(e.target.value)} />
          <XLInput label="Hasta" type="date" value={hasta}
            onChange={(e) => setHasta(e.target.value)} />
          <button type="button" className="xl-btn"
            onClick={load} disabled={loading}>
            {loading ? '\u2026' : 'Calcular'}
          </button>
        </div>
      </header>

      {err && <div className="xl-banner-err" role="alert">{err}</div>}

      {loading && (
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="rect" height={140} />
          <XLSkeleton variant="rect" height={140} />
          <XLSkeleton variant="rect" height={80} />
        </div>
      )}

      {!loading && data && (
        <>
          <div className="xl-er-card">
            <div className="xl-er-card__hd">
              <span className="xl-er-card__title xl-er-card__title--ingreso">INGRESOS</span>
              <span className="xl-er-card__total xl-er-card__total--ingreso">
                {fmt(data.ingresos && data.ingresos.total, 0)}
              </span>
            </div>
            {((data.ingresos && data.ingresos.cuentas) || []).length === 0 && (
              <div className="xl-er-empty">Sin ingresos en el periodo.</div>
            )}
            {((data.ingresos && data.ingresos.cuentas) || []).map((c) => (
              <div key={c.id} className="xl-er-row">
                <span><span className="xl-er-code">{c.codigo}</span>{c.nombre}</span>
                <span style={{ fontFamily: 'var(--xl-font-mono)' }}>{fmt(c.saldo, 0)}</span>
              </div>
            ))}
          </div>

          <div className="xl-er-card">
            <div className="xl-er-card__hd">
              <span className="xl-er-card__title xl-er-card__title--gasto">GASTOS</span>
              <span className="xl-er-card__total xl-er-card__total--gasto">
                {fmt(data.gastos && data.gastos.total, 0)}
              </span>
            </div>
            {((data.gastos && data.gastos.cuentas) || []).length === 0 && (
              <div className="xl-er-empty">Sin gastos en el periodo.</div>
            )}
            {((data.gastos && data.gastos.cuentas) || []).map((c) => (
              <div key={c.id} className="xl-er-row">
                <span><span className="xl-er-code">{c.codigo}</span>{c.nombre}</span>
                <span style={{ fontFamily: 'var(--xl-font-mono)' }}>{fmt(c.saldo, 0)}</span>
              </div>
            ))}
          </div>

          <div className={'xl-utilidad ' + (utilidadPos ? 'xl-utilidad--pos' : 'xl-utilidad--neg')}>
            <div className="xl-utilidad__lbl">Utilidad del periodo</div>
            <div className={'xl-utilidad__val ' + (utilidadPos ? 'xl-utilidad__val--pos' : 'xl-utilidad__val--neg')}>
              {fmt(data.utilidad, 0)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
